import {useState, useCallback, useMemo, useRef, useEffect} from 'react';
import {
  InputWrapper,
  InputWrapperHeader,
  Button,
  PopContainer,
  EmojiSuggestionWrapper,
} from '../comments-style';
import {
  ContentState,
  EditorState,
  convertToRaw,
  convertFromRaw,
} from 'draft-js';
import {getDefaultKeyBinding, KeyBindingUtil} from 'draft-js';
import {
  writeData,
  updateData,
  AddMentions,
  AddSubscribers,
} from '../../../../utils/firebase-request';
import {Entry} from './entry';
import {SmileyEmoji} from '../../../atoms/icons/smiley-emoji';
import {useStoreContext} from '../../../../store/store-context';
import createMentionPlugin, {
  defaultSuggestionsFilter,
} from '@draft-js-plugins/mention';
import createEmojiPlugin, {defaultTheme} from '@draft-js-plugins/emoji';
import Editor from '@draft-js-plugins/editor';
import {ArrowLineRight} from '../../../atoms/icons/arrow-right-line';
import {FlexRow} from '@ui/style/styles';
import {activateNotification} from '@ui/molecules/notification/activate-notification';
import {RichTextToolbar} from '@ui/molecules/editor/rich-text-toolbar';
import {
  normalizeRawContent,
  serializeCommentText,
  shouldHideRichTextPlaceholder,
} from '@ui/molecules/editor/rich-text.utils';
import {COMMENT_MENTION_CLASSNAME} from '@ui/atoms/typography/comment-mention';

const {hasCommandModifier} = KeyBindingUtil;

export function InputComment({
  pathId,
  editComments,
  handleEditComments,
  allMentionedUsers,
  allowedMentionedUsers,
  mentionsPath,
  isReadOnly,
  path,
  allGoalSubscribers,
  defaultSubscribers,
  placeholder = 'You can comment here',
  submitLabel,
  layoutVariant,
  notificationContext,
  onCommentCreated,
  enableRichText = false,
}: any) {
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const [hashGoal] = useState(false);
  const [searchValue, setSearchValue] = useState<any>([]);
  const [, setGoalMention] = useState<any>();
  const ref = useRef<Editor>(null);
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputElement: any = useRef(null);
  const isTeamFeedLayout = layoutVariant === 'teamFeed';
  const hidePlaceholder = shouldHideRichTextPlaceholder(editorState);

  const {
    usersStore: {users},
    authStore: {auth},
    companyStore: {handleCommentRef},
  } = useStoreContext();
  const {
    MentionSuggestions,
    EmojiSuggestions,
    EmojiSelect,
    plugins,
  } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      entityMutability: 'IMMUTABLE',
      theme: {mention: COMMENT_MENTION_CLASSNAME},
      mentionPrefix: '@',
      supportWhitespace: true,
    });

    // eslint-disable-next-line no-shadow
    const {MentionSuggestions} = mentionPlugin;

    const emojiPlugin = createEmojiPlugin({
      useNativeArt: true,
      positionSuggestions: () => {
        return {marginRight: '300px'};
      },

      selectButtonContent: (
        <>
          <div aria-hidden="true">
            <SmileyEmoji />
          </div>
          <span className="beam-visually-hidden">Choose an emoji</span>
        </>
      ),
      theme: {
        ...defaultTheme,
        emojiSelectButton: `${defaultTheme.emojiSelectButton} beam-emoji-trigger`,
        emojiSelectButtonPressed: `${defaultTheme.emojiSelectButtonPressed} beam-emoji-trigger beam-emoji-trigger--open`,
        emojiSelectPopover: isTeamFeedLayout
          ? `${defaultTheme.emojiSelectPopover} emojiSuggestion team-feed-emoji-popover`
          : `${defaultTheme.emojiSelectPopover} emojiSuggestion`,
        emojiSelectPopoverTitle: isTeamFeedLayout
          ? `${defaultTheme.emojiSelectPopoverTitle} team-feed-emoji-popover-title`
          : defaultTheme.emojiSelectPopoverTitle,
        emojiSelectPopoverGroupTitle: `${defaultTheme.emojiSelectPopoverGroupTitle} beam-emoji-group-title`,
        emojiSelectPopoverEntryFocused: `${defaultTheme.emojiSelectPopoverEntryFocused} beam-emoji-entry--focused`,
        emojiSelectPopoverToneSelectList: `${defaultTheme.emojiSelectPopoverToneSelectList} beam-emoji-tone-list`,
        emojiSelectPopoverNavEntry: `${defaultTheme.emojiSelectPopoverNavEntry} beam-emoji-nav-entry`,
        emojiSelectPopoverNavEntryActive: `${defaultTheme.emojiSelectPopoverNavEntryActive} beam-emoji-nav-entry beam-emoji-nav-entry--active`,
        emojiSelectPopoverScrollbar: `${defaultTheme.emojiSelectPopoverScrollbar} beam-emoji-scrollbar`,
        emojiSelectPopoverScrollbarThumb: `${defaultTheme.emojiSelectPopoverScrollbarThumb} beam-emoji-scrollbar-thumb`,
      },
      toneSelectOpenDelay: 0,
    });

    // eslint-disable-next-line no-shadow
    const {EmojiSuggestions, EmojiSelect} = emojiPlugin;
    // eslint-disable-next-line no-shadow
    const plugins = [mentionPlugin, emojiPlugin];
    return {
      plugins,
      MentionSuggestions,
      EmojiSuggestions,
      EmojiSelect,
    };
  }, [isTeamFeedLayout]);

  const onOpenChange = useCallback((_open: boolean) => {
    setOpen(_open);
  }, []);

  const handleUsers = useCallback(() => {
    const formatUsers = (users: any[]) => {
      return users.map((user: any) => {
        return {
          name:
            user.firstName && user.lastName
              ? user.firstName + ' ' + user.lastName
              : user.email,
          id: user.id,
        };
      });
    };

    if (allowedMentionedUsers) {
      return formatUsers(
        users.filter((user) => allowedMentionedUsers.includes(user.id)),
      );
    }

    return formatUsers(users.filter((user) => user.id !== auth?.user?.id));
  }, [users, auth?.user?.id, allowedMentionedUsers]);

  const handleMentions = useCallback(() => {
    const formatUsers = (users: any[]) => {
      return users.map((user: any) => {
        return {
          name:
            user.firstName && user.lastName
              ? user.firstName + ' ' + user.lastName
              : user.email,
          id: user.id,
        };
      });
    };

    if (allowedMentionedUsers) {
      return formatUsers(
        users.filter((user) => allowedMentionedUsers.includes(user.id)),
      );
    }

    return setSearchValue(formatUsers(users));
  }, [users, allowedMentionedUsers]);

  useEffect(() => {
    handleMentions();
  }, [handleMentions]);

  useEffect(() => {
    if (editComments) {
      if (!editComments) {
        setEditorState(EditorState.createEmpty());
        handleEditComments(undefined);
      } else {
        const rawContent = normalizeRawContent(editComments[1].entities);
        const legacyText = String(editComments[1].comment || '').replace(
          /_\(([^)]+)\)_/g,
          (_match: string, userId: string) => {
            const user = users.find((item: any) => item.id === userId);
            return user
              ? `@${
                  user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.email
                }`
              : '@Team member';
          },
        );
        const newContent = rawContent
          ? EditorState.createWithContent(convertFromRaw(rawContent))
          : EditorState.createWithContent(
              ContentState.createFromText(legacyText),
            );
        setGoalMention(editComments[1].goalMentions);
        setEditorState(newContent);
      }
    }
  }, [editComments, handleEditComments, users]);

  const onChange = useCallback((_editorState: EditorState) => {
    setEditorState(_editorState);
  }, []);

  const onSearchChange = useCallback(
    ({value}: {value: string}) => {
      setSearchValue(defaultSuggestionsFilter(value, handleUsers()));
    },
    [handleUsers],
  );

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const contentState = editorState.getCurrentContent();
    const raw = convertToRaw(contentState);
    handleCommentRef('');

    const res: any = raw.entityMap;
    const obj = Object.entries(res).map((item: any) => item[1]);
    const mentionUser = obj.map(
      (item: any) => item.type === 'mention' && item?.data?.mention,
    );
    const filterMention = mentionUser.filter((item: any): item is {
      id: string;
      name: string;
    } => Boolean(item?.id && item?.name));
    const result = serializeCommentText(raw, filterMention);

    if (result) {
      if (editComments) {
        updateData(
          editComments[0],
          {
            comment: result,
            commentBy: auth.user.id,
            createdAt: editComments[1].createdAt,
            edited: true,
            rawComment: raw,
            updatedAt: Date.now(),
          },
          path,
        );

        setEditorState(EditorState.createEmpty());
        handleEditComments(undefined);
      } else {
        const mentions = filterMention.map((mention) => mention.id);
        setIsSubmitting(true);
        try {
          const commentId = await writeData(
            {
              comment: result,
              commentBy: auth.user.id,
              createdAt: Date.now(),
              edited: false,
              rawComment: raw,
              updatedAt: Date.now(),
            },
            path,
          );

          if (onCommentCreated) {
            try {
              await onCommentCreated({
                mentionedUserIds: mentions,
                commentId,
                rawComment: raw,
              });
            } catch {
              activateNotification({
                title: 'Comment posted',
                content:
                  'The comment is saved, but some recipients may not see an alert.',
                kind: 'warning',
              });
            }
          } else {
            const getNotification = (recipientId: string) => {
              if (!notificationContext) return undefined;
              const href =
                recipientId === notificationContext.participantId
                  ? notificationContext.ownerHref
                  : notificationContext.defaultHref ||
                    notificationContext.ownerHref;
              return {
                ...notificationContext,
                actorId: auth.user.id,
                actorName:
                  auth.user.firstName && auth.user.lastName
                    ? `${auth.user.firstName} ${auth.user.lastName}`
                    : auth.user.email || 'A teammate',
                createdAt: Date.now(),
                href,
              };
            };
            const handleNewAddMentions = (data: any) => {
              data.forEach((id: string) => {
                const notification = getNotification(id);
                AddMentions(
                  id,
                  pathId,
                  {
                    unreadTasks: 0,
                    tasksMentionedBy: [],
                    mentionedBy: mentions.includes(id) ? [auth.user.id] : [],
                    unreadComment: id === auth.user.id ? 0 : 1,
                    ...(notification ? {notification} : {}),
                  },
                  mentionsPath,
                );
              });
            };
            if (allGoalSubscribers?.length) {
              const users = [...new Set([...allGoalSubscribers, ...mentions])];
              AddSubscribers(path, users);
              if (allMentionedUsers?.length) {
                allMentionedUsers.forEach((user: any) => {
                  const id: any = Object.keys(user)[0];
                  const getMentionedBy = user[id]?.mentionedBy || [];

                  AddMentions(
                    id,
                    pathId,
                    {
                      unreadTasks: user[id]?.unreadTasks || 0,
                      tasksMentionedBy: user[id]?.tasksMentionedBy || [],
                      mentionedBy: mentions.includes(id)
                        ? [...new Set([...getMentionedBy, auth.user.id])]
                        : [...new Set([...getMentionedBy])],
                      unreadComment:
                        id === auth.user.id
                          ? 0
                          : Number(user[id]?.unreadComment || 0) + 1,
                      ...(getNotification(id)
                        ? {notification: getNotification(id)}
                        : {}),
                    },
                    mentionsPath,
                  );
                });
              } else {
                handleNewAddMentions(users);
              }
            } else if (defaultSubscribers) {
              const users = [...new Set([...defaultSubscribers, ...mentions])];
              AddSubscribers(path, users);
              handleNewAddMentions(users);
            }
          }

          setEditorState(EditorState.createEmpty());
        } catch {
          activateNotification({
            title: 'Unable to post comment',
            content: 'Please check your connection and try again.',
            kind: 'error',
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    }
  };

  function myKeyBindingFn(e: any): any {
    if (e.key === 'enter' && e.ctrlKey && hasCommandModifier(e)) {
      void handleSubmit();
      return '';
    }
    return getDefaultKeyBinding(e);
  }
  return (
    <InputWrapper
      className={isTeamFeedLayout ? 'team-feed-comment-input' : undefined}>
      <InputWrapperHeader
        className={
          isTeamFeedLayout ? 'team-feed-comment-input__header' : undefined
        }>
        {enableRichText && !isReadOnly && (
          <RichTextToolbar
            editorState={editorState}
            onChange={onChange}
            disabled={isSubmitting}
            className="comment-rich-text-toolbar"
          />
        )}
        <div
          className={
            isTeamFeedLayout ? 'team-feed-comment-input__row' : undefined
          }
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <div
            className={'editor w-full '}
            style={{padding: '3px 5px'}}
            onClick={() => {
              hashGoal === false && ref.current!.focus();
            }}>
            <Editor
              editorKey={'editor'}
              editorState={editorState}
              handleKeyCommand={myKeyBindingFn}
              onChange={onChange}
              readOnly={isReadOnly}
              textAlignment="left"
              placeholder={hidePlaceholder ? undefined : placeholder}
              ref={ref}
              plugins={plugins}
            />
            <EmojiSuggestionWrapper>
              <EmojiSuggestions />
            </EmojiSuggestionWrapper>
          </div>

          <FlexRow
            className={
              isTeamFeedLayout ? 'team-feed-comment-input__actions' : undefined
            }>
            <div className={'testing'}>
              <EmojiSelect />
            </div>
            <Button
              type="button"
              aria-label={submitLabel || 'Post comment'}
              onClick={() => void handleSubmit()}
              disabled={isReadOnly || isSubmitting}
              style={
                submitLabel ? {padding: '0 14px', width: 'auto'} : undefined
              }>
              {isSubmitting
                ? 'Posting…'
                : submitLabel || <ArrowLineRight color="white" />}
            </Button>
          </FlexRow>
        </div>

        <div
          className={isTeamFeedLayout ? 'team-feed-mention-anchor' : undefined}
          style={{position: 'relative'}}>
          <MentionSuggestions
            open={open}
            onOpenChange={onOpenChange}
            suggestions={searchValue}
            onSearchChange={onSearchChange}
            entryComponent={Entry}
            popoverContainer={({children}) => (
              <PopContainer
                className={
                  isTeamFeedLayout ? 'team-feed-mention-popover' : undefined
                }
                style={{zIndex: 5, position: 'absolute'}}>
                {' '}
                {children}
              </PopContainer>
            )}
          />
        </div>
        {hashGoal && (
          <>
            <div
              ref={inputElement}
              style={{
                background: 'white',
                position: 'absolute',
                top: '40px',
                zIndex: 5,
                width: '300px',
                borderRadius: '10px',
              }}></div>
          </>
        )}
      </InputWrapperHeader>
    </InputWrapper>
  );
}
